import {useState} from "react";
import {Card, Col, Row, Progress, Tag, Table, Alert, Tooltip, Select} from "antd";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon} from "~/components";
import {useGetMySubscriptionQuery, useGetPlansQuery, useGetMyTokenLedgerQuery} from "~/reduxs/api/subscriptionApiSlice";
import style from "../style/Subscription.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Màu thanh tiến trình theo mức đã dùng (xanh → cam → đỏ khi sắp hết). */
const barColor = (pct) => (pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a');

/** Gói được gợi ý (được đánh dấu "Phổ biến" trong lưới giá). */
const RECOMMENDED = 'pro';

/** Tagline dự phòng theo slug khi gói chưa có mô tả trong DB. */
const PLAN_TAGLINE = {
	free: 'Bắt đầu miễn phí',
	base: 'Cho cá nhân & freelancer',
	pro: 'Cho đội nhóm chuyên nghiệp',
	max: 'Cho agency & doanh nghiệp',
};

/** Các tính năng boolean hiển thị dạng checklist trong thẻ giá. */
const CARD_FEATURES = [
	{key: 'can_connect_gsc', label: 'Kết nối Google Search Console'},
	{key: 'can_ai_deep', label: 'Trợ lý AI chuyên sâu'},
	{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá'},
	{key: 'can_export_pdf', label: 'Xuất báo cáo PDF'},
	{key: 'can_export_excel', label: 'Xuất báo cáo Excel'},
	{key: 'can_use_api', label: 'API hệ thống'},
];

/** Nhãn + màu cho loại giao dịch token (khớp MemberDetail để nhất quán). */
const REASON_LABELS = {
	consume: 'Tiêu dùng AI',
	admin_add: 'Admin cộng',
	admin_deduct: 'Admin trừ',
	reset: 'Reset tháng',
	bonus: 'Token thưởng',
};
const REASON_COLOR = {
	consume: 'red',
	admin_add: 'green',
	admin_deduct: 'orange',
	reset: 'default',
	bonus: 'blue',
};

/** Cột bảng lịch sử token của user. */
const LEDGER_COLUMNS = [
	{
		title: 'Thời gian', dataIndex: 'created', key: 'created', width: 160,
		render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
	},
	{
		title: 'Loại', dataIndex: 'reason', key: 'reason', width: 140,
		render: (v) => <Tag color={REASON_COLOR[v] || 'default'}>{REASON_LABELS[v] || v}</Tag>,
	},
	{title: 'Chi tiết', dataIndex: 'note', key: 'note', render: (v) => v || '—'},
	{
		title: 'Token', dataIndex: 'delta', key: 'delta', align: 'right', width: 120,
		render: (v) => <span style={{color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600}}>{v >= 0 ? '+' : ''}{nf(v)}</span>,
	},
];

/** 1 đồng hồ hạn mức (token/URL/dự án) — số còn lại nổi bật + thanh tiến trình. */
function UsageMeter({icon, label, used, limit, remaining, resetAt, unlimited, ownKey}) {
	const rem = remaining != null ? remaining : Math.max(0, (limit || 0) - (used || 0));
	const pct = unlimited || ownKey || !limit ? 0 : Math.min(100, Math.round(((used || 0) / limit) * 100));
	const special = unlimited || ownKey;

	return (
		<div className={cn('meter')}>
			<div className={cn('meter-top')}>
				<span className={cn('meter-icon')}><FontAwesomeIcon icon={icon} /></span>
				<span className={cn('meter-label')}>{label}</span>
			</div>

			<div className={cn('meter-figure')}>
				{special ? (
					<span className={cn('meter-unlimited')}>{ownKey ? 'Khoá AI riêng' : 'Không giới hạn'}</span>
				) : (
					<>
						<span className={cn('meter-remaining')}>{nf(rem)}</span>
						<span className={cn('meter-of')}>còn lại / {nf(limit)}</span>
					</>
				)}
			</div>

			<Progress
				percent={special ? 100 : pct}
				showInfo={false}
				size="small"
				strokeColor={special ? '#7c3aed' : barColor(pct)}
				trailColor="#eef1f4"
			/>

			<div className={cn('meter-foot')}>
				{unlimited
					? 'Tài khoản quản trị'
					: ownKey
						? 'Dùng khoá AI của riêng bạn'
						: `Đã dùng ${nf(used)}${resetAt ? ' · làm mới ' + dayjs(resetAt).format('DD/MM') : ''}`}
			</div>
		</div>
	);
}

/** 1 thẻ giá gói (pricing card) — quota nổi bật + checklist tính năng + CTA. */
function PlanCard({plan, current, recommended}) {
	const L = plan.limits || {};

	return (
		<div className={cn('plan', {current, recommended})} data-plan={plan.slug}>
			{recommended && (
				<span className={cn('plan-badge')}>
					<FontAwesomeIcon icon="fa-solid fa-star" /> Phổ biến
				</span>
			)}

			<span className={cn('plan-name')}>{plan.name}</span>
			<div className={cn('plan-tag')}>{plan.description || PLAN_TAGLINE[plan.slug] || ''}</div>

			<div className={cn('plan-price')}>
				{plan.price > 0 ? (
					<>
						<span className={cn('plan-amount')}>{nf(plan.price)}đ</span>
						<span className={cn('plan-cycle')}>/tháng</span>
					</>
				) : (
					<span className={cn('plan-amount')}>Miễn phí</span>
				)}
			</div>

			<div className={cn('plan-quota')}>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />
					<span><strong>{nf(L.ai_token_limit)}</strong> token AI / tháng</span>
				</div>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-link" />
					<span><strong>{nf(L.url_checks_per_period)}</strong> URL kiểm tra / tuần</span>
				</div>
				<div className={cn('quota-row')}>
					<FontAwesomeIcon icon="fa-light fa-folder-tree" />
					<span><strong>{nf(L.max_projects)}</strong> dự án · {nf(L.max_urls_per_project)} URL/dự án</span>
				</div>
			</div>

			<ul className={cn('plan-feats')}>
				{CARD_FEATURES.map((f) => (
					<li key={f.key} className={cn('feat', {on: !!L[f.key]})}>
						<FontAwesomeIcon icon={L[f.key] ? 'fa-light fa-circle-check' : 'fa-light fa-circle-minus'} />
						<span>{f.label}</span>
					</li>
				))}
			</ul>

			<div className={cn('plan-cta')}>
				{current ? (
					<div className={cn('plan-btn', 'is-current')}>
						<FontAwesomeIcon icon="fa-light fa-circle-check" /> Đang sử dụng
					</div>
				) : (
					<Tooltip title="Liên hệ quản trị viên để đổi hoặc nâng cấp gói.">
						<div className={cn('plan-btn')}>Nâng cấp</div>
					</Tooltip>
				)}
			</div>
		</div>
	);
}

function SubscriptionPage() {
	const {data: sub, isLoading, isError} = useGetMySubscriptionQuery();
	const {data: plans = []} = useGetPlansQuery();
	const {data: ledger = []} = useGetMyTokenLedgerQuery();

	// Lọc lịch sử token theo loại giao dịch (client-side — BE trả tối đa 100 dòng).
	const [reasonFilter, setReasonFilter] = useState('all');
	const filteredLedger = reasonFilter === 'all' ? ledger : ledger.filter((r) => r.reason === reasonFilter);

	if (isLoading) return <Loading />;

	if (isError || !sub) {
		return (
			<div className="container">
				<Alert type="error" message="Không tải được thông tin gói. Vui lòng thử lại." showIcon />
			</div>
		);
	}

	const isRoot = Boolean(sub.is_root);
	const ownKey = Boolean(sub.require_own_ai_key);
	const currentSlug = sub.plan?.slug;
	const sortedPlans = [...plans].sort((a, b) => (a.price || 0) - (b.price || 0));

	// Các hàng bảng so sánh chi tiết (đọc từ limits của từng gói).
	const boolCell = (v) =>
		v ? (
			<FontAwesomeIcon icon="fa-light fa-circle-check" className={cn('cell-yes')} />
		) : (
			<FontAwesomeIcon icon="fa-light fa-minus" className={cn('cell-no')} />
		);

	const rows = [
		{key: 'price', label: 'Giá / tháng', render: (p) => (p.price > 0 ? `${nf(p.price)}đ` : 'Miễn phí')},
		{key: 'ai_token_limit', label: 'AI Token / tháng', render: (p) => nf(p.limits.ai_token_limit)},
		{key: 'url_checks_per_period', label: 'URL kiểm tra / tuần', render: (p) => nf(p.limits.url_checks_per_period)},
		{key: 'max_projects', label: 'Số dự án', render: (p) => nf(p.limits.max_projects)},
		{key: 'max_urls_per_project', label: 'URL tối đa / dự án', render: (p) => nf(p.limits.max_urls_per_project)},
		{key: 'crawl_min_interval_min', label: 'Tần suất crawl', render: (p) => `${p.limits.crawl_min_interval_min}'`},
		{key: 'history_retention_days', label: 'Lưu lịch sử', render: (p) => (p.limits.history_retention_days > 0 ? `${p.limits.history_retention_days} ngày` : '∞')},
		{key: 'can_connect_gsc', label: 'Kết nối GSC', render: (p) => boolCell(p.limits.can_connect_gsc)},
		{key: 'can_ai_deep', label: 'AI chuyên sâu', render: (p) => boolCell(p.limits.can_ai_deep)},
		{key: 'can_export_pdf', label: 'Xuất PDF', render: (p) => boolCell(p.limits.can_export_pdf)},
		{key: 'can_export_excel', label: 'Xuất Excel', render: (p) => boolCell(p.limits.can_export_excel)},
		{key: 'can_keyword_tracking', label: 'Theo dõi từ khoá', render: (p) => boolCell(p.limits.can_keyword_tracking)},
		{key: 'can_use_api', label: 'API hệ thống', render: (p) => boolCell(p.limits.can_use_api)},
	];

	const columns = [
		{title: 'Tính năng', dataIndex: 'label', key: 'label', fixed: 'left', width: 190},
		...sortedPlans.map((p) => ({
			title: (
				<div className={cn('plan-col', {current: p.slug === currentSlug})}>
					<span className={cn('plan-col-name')}>{p.name}</span>
					{p.slug === currentSlug && <Tag color="red" className={cn('plan-current-tag')}>Đang dùng</Tag>}
				</div>
			),
			key: p.slug,
			align: 'center',
			onCell: () => ({className: p.slug === currentSlug ? cn('col-current') : undefined}),
			render: (_, row) => row.render(p),
		})),
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-crown"
				title="Gói & Hạn mức"
				subtitle="Theo dõi gói hiện tại, hạn mức token AI, URL kiểm tra và số dự án của bạn."
			/>

			{/* Hero — gói hiện tại */}
			<div className={cn('hero')} data-plan={currentSlug}>
				<div className={cn('hero-left')}>
					<span className={cn('hero-eyebrow')}>
						<FontAwesomeIcon icon="fa-light fa-crown" /> {isRoot ? 'Tài khoản quản trị' : 'Gói hiện tại'}
					</span>
					<div className={cn('hero-name')}>{sub.plan?.name}</div>
					<div className={cn('hero-price')}>
						{isRoot
							? 'Toàn quyền · không giới hạn hạn mức'
							: sub.plan?.price > 0
								? `${nf(sub.plan.price)}đ / tháng`
								: 'Miễn phí'}
					</div>
				</div>
				<div className={cn('hero-right')}>
					{isRoot ? (
						<span className={cn('hero-remaining')}><FontAwesomeIcon icon="fa-light fa-infinity" /></span>
					) : ownKey ? (
						<>
							<span className={cn('hero-remaining')}><FontAwesomeIcon icon="fa-light fa-key" /></span>
							<span className={cn('hero-remaining-label')}>Dùng khoá AI riêng của bạn</span>
						</>
					) : (
						<>
							<span className={cn('hero-remaining')}>{nf(sub.ai_tokens?.remaining)}</span>
							<span className={cn('hero-remaining-label')}>token AI còn lại tháng này</span>
						</>
					)}
				</div>
			</div>

			{/* Đồng hồ hạn mức */}
			<Row gutter={[16, 16]} className={cn('meters')}>
				<Col xs={24} md={8}>
					<UsageMeter
						icon="fa-light fa-wand-magic-sparkles"
						label="AI Token (tháng)"
						used={sub.ai_tokens?.used}
						limit={sub.ai_tokens?.limit}
						remaining={sub.ai_tokens?.remaining}
						resetAt={sub.ai_tokens?.reset_at}
						unlimited={isRoot}
						ownKey={ownKey}
					/>
				</Col>
				<Col xs={24} md={8}>
					<UsageMeter
						icon="fa-light fa-link"
						label="URL kiểm tra (tuần)"
						used={sub.url_checks?.used}
						limit={sub.url_checks?.limit}
						remaining={sub.url_checks?.remaining}
						resetAt={sub.url_checks?.reset_at}
						unlimited={isRoot}
					/>
				</Col>
				<Col xs={24} md={8}>
					<UsageMeter
						icon="fa-light fa-folder-tree"
						label="Dự án"
						used={sub.projects?.used}
						limit={sub.projects?.limit}
						unlimited={isRoot}
					/>
				</Col>
			</Row>

			{/* Lưới thẻ giá */}
			{sortedPlans.length > 0 && (
				<div className={cn('section')}>
					<div className={cn('section-head')}>
						<h3 className={cn('section-title')}>Các gói dịch vụ</h3>
						<p className={cn('section-sub')}>Chọn gói phù hợp với quy mô SEO của bạn — nâng cấp bất cứ lúc nào.</p>
					</div>

					<Row gutter={[18, 18]}>
						{sortedPlans.map((p) => (
							<Col xs={24} sm={12} xl={6} key={p.slug}>
								<PlanCard plan={p} current={p.slug === currentSlug} recommended={p.slug === RECOMMENDED} />
							</Col>
						))}
					</Row>
				</div>
			)}

			{/* Bảng so sánh chi tiết */}
			{sortedPlans.length > 0 && (
				<Card
					className={cn('plans-card')}
					title={<span><FontAwesomeIcon icon="fa-light fa-table-list" /> Bảng so sánh chi tiết</span>}
				>
					<Table
						className={cn('compare-table')}
						columns={columns}
						dataSource={rows.map((r) => ({...r}))}
						rowKey="key"
						pagination={false}
						size="middle"
						scroll={{x: 'max-content'}}
					/>
					<div className={cn('plans-note')}>
						<FontAwesomeIcon icon="fa-light fa-circle-info" /> Muốn đổi gói? Liên hệ quản trị viên để được hỗ trợ nâng cấp.
					</div>
				</Card>
			)}

			{/* Lịch sử token của chính user */}
			<Card
				className={cn('plans-card', 'ledger-card')}
				title={<span><FontAwesomeIcon icon="fa-light fa-clock-rotate-left" /> Lịch sử token</span>}
				extra={
					<span style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
						<Select
							size="small"
							value={reasonFilter}
							onChange={setReasonFilter}
							style={{minWidth: 150}}
							options={[
								{value: 'all', label: 'Tất cả loại'},
								...Object.entries(REASON_LABELS).map(([value, label]) => ({value, label})),
							]}
						/>
						<span className={cn('ledger-hint')}>100 giao dịch gần nhất</span>
					</span>
				}
			>
				<Table
					className={cn('compare-table')}
					columns={LEDGER_COLUMNS}
					dataSource={filteredLedger}
					rowKey="id"
					size="small"
					pagination={{pageSize: 10, hideOnSinglePage: true}}
					locale={{emptyText: reasonFilter !== 'all'
						? 'Không có giao dịch nào thuộc loại này.'
						: 'Chưa có giao dịch token nào. Hãy dùng thử Trợ lý AI hoặc Sinh nội dung.'}}
				/>
			</Card>
		</div>
	);
}

export default SubscriptionPage;
